/**
 * Lift2Fit — the post-login application shell.
 *
 * There are two shells and the route chooses between them, so the two products
 * never stack as two nav bars:
 *
 *   * the LIFTER shell — Dashboard · My Grid · My Programme · Summary · Goals ·
 *     My Coach · Get a plan, with the account menu. Exactly the lifter's app.
 *   * the COACH shell — the coaching workspace as the primary nav: Summary ·
 *     Calendar · Clients · Team · Packs · Payments · Insights · To-do, tabs
 *     only, plus the account menu. No lifter training tab and no lifter
 *     cross-link appears here: the row is the workspace and nothing else.
 *
 * The shell is chosen by audience/route, not by role alone: anything under the
 * coaching workspace (`/crm/*`, and the coach authoring desk at `/coach`) renders
 * the coach shell, and every lifter route renders the lifter shell — a coach who
 * wants their own training opens the account menu ("Your training → Dashboard")
 * and gets the lifter shell back, the same place every other secondary
 * destination lives. A
 * route that needs the coach role is refused with the in-app panel when the
 * account does not hold it, inside the lifter shell, so a plain lifter never
 * sees a coaching nav at all.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { api, planIncludesTeam, type Role, type TrainingTrack } from '@/lib/api';
import { cn, displayInitials } from '@/lib/format';
import { useSession } from '@/lib/useSession';
import { prefetchCoachRoster } from '@/lib/prefetch';
import { useTheme } from '@/lib/useTheme';
import { useUnitPreference } from '@/lib/useUnitPreference';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Button } from '@/components/ui';
import { ChevronDownIcon, GridIcon } from '@/lib/icons';
import { TRACKS, trackMeta } from './demoData';
import { CoachChatIconButton, LifterChatIconButton } from './chat';
import { AdminChatButton } from './admin/AdminChatButton';
import { Link, navigate, useRoute } from './router';

interface NavItem {
  to: string;
  label: string;
  /** Render only for a plan that includes internal coaches (0033). */
  needsTeam?: boolean;
}

/** True when this route belongs to the coach product rather than the lifter app. */
export function isCoachRoute(path: string): boolean {
  return path === '/coach' || path.startsWith('/coach/') || path === '/crm' || path.startsWith('/crm/');
}

/**
 * True when this route belongs to the founder's admin portal. The admin shell
 * is selected only for an account that actually holds the role, so a non-admin
 * deep link stays in the lifter shell and sees the refusal panel.
 */
export function isAdminRoute(path: string): boolean {
  return path === '/admin' || path.startsWith('/admin/');
}

/**
 * The coaching workspace. It is its own section — never part of the lifter's
 * training nav — and only exists for accounts holding the coach role. A coaching
 * business is one entity whether it is a solo coach or a club, so there is no
 * coach path and gym path here: one workspace of working surfaces. The nav is
 * tabs only; the `<nav>`'s `aria-label` carries the workspace name for screen
 * readers, so nothing visible is spent repeating it.
 *
 * Profile is deliberately NOT a tab: account-level surfaces belong to the
 * account menu, which already carries "Profile & settings" — see the note on
 * `CRM_NAV` below.
 */
const CRM_NAV: NavItem[] = [
  { to: '/crm', label: 'Summary' },
  { to: '/crm/calendar', label: 'Calendar' },
  { to: '/crm/clients', label: 'Clients' },
  // 0033: the gym's coaches. Rendered only for a plan that includes teams; the
  // route still answers with a plain panel if someone types it.
  { to: '/crm/team', label: 'Team', needsTeam: true },
  { to: '/crm/packs', label: 'Packs' },
  { to: '/crm/payments', label: 'Payments' },
  { to: '/crm/insights', label: 'Insights' },
  { to: '/crm/todo', label: 'To-do' },
  // 0028 IA: Chat and Prospects are NOT tabs. They live in the standalone
  // `#/chat` workspace, opened in its own window from the Clients page and from
  // a client's page. This nav is the working surfaces only.
  // `#/crm/profile` has no tab on purpose: the coach's profile and settings are
  // reached from the account menu's "Profile & settings" (`account-profile-link`
  // above), which is where account-level actions already live. The route stays —
  // it is the target of that menu item, not an orphan.
];

/**
 * The founder's admin portal. This is a THIRD shell, not a band added on top of
 * another one: when the route is an admin route and the account holds the role,
 * these nine tabs ARE the primary nav and no lifter or CRM tab is rendered.
 * `#/admin/accounts` still exists for the account detail, but the primary list
 * is the dedicated Lifters tab.
 */
const ADMIN_NAV: NavItem[] = [
  { to: '/admin', label: 'Overview' },
  { to: '/admin/businesses', label: 'Businesses' },
  { to: '/admin/lifters', label: 'Lifters' },
  // 0038 — the support team that runs Help, and the caller's own desk entry.
  { to: '/admin/staff', label: 'Staff' },
  { to: '/admin/revenue', label: 'Revenue' },
  // 'Plans' rather than 'Plans & pricing': the admin row has nine labels and
  // the long form pushed System and Audit onto a second line at 1440.
  { to: '/admin/plans', label: 'Plans' },
  { to: '/admin/offers', label: 'Offers' },
  { to: '/admin/invitations', label: 'Invites' },
  { to: '/admin/system', label: 'System' },
  { to: '/admin/audit', label: 'Audit' },
];

/**
 * The lifter shell's primary nav. Every account is a lifter first, so their own
 * log and their own programme are always one tap away — named "My …" so they
 * never read as the landing page's marketing sections. The coaching workspace is
 * deliberately absent: a coach reaches it through the account menu or, in the
 * coach shell, it is the whole product. The profile has no tab either — it lives
 * in the account menu at the far right.
 */
function buildLifterNav(session: ReturnType<typeof useSession>): NavItem[] {
  const items: NavItem[] = [{ to: '/dashboard', label: 'Dashboard' }];
  if (session.hasRole('lifter')) {
    items.push({ to: '/grid', label: 'My Grid' });
    items.push({ to: '/programme', label: 'My Programme' });
    items.push({ to: '/summary', label: 'Summary' });
    items.push({ to: '/goals', label: 'Goals' });
    items.push({ to: '/my-coach', label: 'My Coach' });
  }
  items.push({ to: '/plan/new', label: 'Get a plan' });
  return items;
}

/**
 * Role labels for the account menu and role-gated panels. `trainer` is an
 * internal legacy database role, not a product role, so it is never shown to a
 * person: the product's two human words are lifter and coach.
 */
const ROLE_LABELS: Record<Role, string> = {
  lifter: 'Lifter',
  coach: 'Coach',
  admin: 'Admin',
  trainer: 'Coach',
};

function roleSummary(roles: readonly Role[]): string {
  const shown = roles.filter((role) => role !== 'trainer');
  const list = shown.length ? shown : (['lifter'] as Role[]);
  return list.map((role) => ROLE_LABELS[role]).join(' · ');
}

function DemoBanner() {
  return (
    <div
      className="l2f-demo-banner px-4 py-2 text-[12px] font-bold uppercase tracking-[0.08em] text-ink-muted md:px-8"
      data-testid="demo-banner"
      role="status"
    >
      Demo data — start the API to use your own
    </div>
  );
}

/**
 * Demo controls are track-only. The old role switcher implied a person could be
 * a "trainer" or flip between products from inside the app; the two demos are
 * split at the front doors instead (a lifter demo at `#/login`, no offline coach
 * demo), so the only demo control left is the training track the lifter demo
 * shows.
 */
function DemoTrackControl() {
  const session = useSession();
  if (!session.isDemo) return null;
  const track = (session.user?.track ?? 'general') as TrainingTrack;

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2 md:px-8" data-testid="demo-controls">
      <label className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">
        Demo track
        <select
          value={track}
          onChange={(event) => session.switchTrack(event.target.value as TrainingTrack)}
          className="h-11 border border-hairline bg-surface px-2 text-[12px] font-bold text-ink"
          aria-label="Demo training track"
        >
          {TRACKS.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      <span className="text-[11px] text-ink-muted">
        {trackMeta(track).persona} · {trackMeta(track).metricsFocus.join(' · ')}
      </span>
    </div>
  );
}

function AccountMenu({
  audience,
  onSignOut,
}: {
  audience: 'lifter' | 'crm' | 'admin';
  onSignOut: () => void;
}) {
  const session = useSession();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const user = session.user;

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!user) return null;
  // Two-letter monogram only: the nav control is a fixed size, so the full name
  // would resize the whole nav as it changes. The full name stays reachable on
  // the button's accessible name/title (and in the menu below).
  const initials = displayInitials(user.displayName, user.email);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="account-menu-button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={user.displayName}
        title={`Account menu — ${user.displayName}`}
        className="inline-flex h-11 w-16 shrink-0 items-center justify-center gap-1.5 border border-hairline bg-surface px-0 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent"
      >
        <span
          data-testid="account-menu-initials"
          aria-hidden="true"
          className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-pill bg-ink text-[11px] leading-none text-ink-inverse"
        >
          {initials}
        </span>
        <ChevronDownIcon className="shrink-0 text-[14px]" aria-hidden="true" />
      </button>

      {open ? (
        <div
          role="menu"
          aria-label="Account"
          className="absolute right-0 z-50 mt-2 w-64 border border-hairline bg-surface p-4 shadow-modal"
        >
          <p className="text-[14px] font-bold text-ink">{user.displayName}</p>
          <p className="text-[12px] text-ink-muted">{user.email}</p>
          <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.08em] text-accent">
            {user.tier} · {roleSummary(user.roles)}
          </p>
          <div className="mt-3 flex flex-col">
            {/* Every entry belongs to one side of the product, and the menu says
                which: "Your training" is the lifter interface, "Coaching
                workspace" is the CRM. AI plan is emphatically a YOUR TRAINING
                entry — it is the lifter-side plan wizard, never a workspace
                feature — so it can never be mistaken for one. A free-tier coach
                has no lifter interface at all (0021), so those entries are
                absent and the reason is stated instead. */}
            {user.lifterInterface ? (
              <>
                <p
                  data-testid="account-menu-your-training"
                  className="border-t border-hairline pt-2 text-[10px] font-bold uppercase tracking-[0.12em] text-ink-muted"
                >
                  Your training
                </p>
                <Link
                  to="/dashboard"
                  role="menuitem"
                  data-testid="account-dashboard-link"
                  onClick={() => setOpen(false)}
                  className="py-2 text-[13px] font-bold text-ink hover:text-accent"
                >
                  Dashboard
                </Link>
                <Link
                  to="/plan/new"
                  role="menuitem"
                  data-testid="account-ai-plan"
                  onClick={() => setOpen(false)}
                  className="py-2 text-[13px] font-bold text-ink hover:text-accent"
                >
                  AI plan
                </Link>
              </>
            ) : (
              <p
                data-testid="account-menu-no-lifter"
                className="border-t border-hairline pt-2 text-[12px] leading-relaxed text-ink-muted"
              >
                Your plan is Free, so the lifter interface isn’t included. Upgrade to Coach for your own
                training — see Profile &amp; settings.
              </p>
            )}
            {/* The coaching workspace is a separate product surface. A coach
                gets to it from here in either direction: from the lifter shell
                into the workspace, or from the workspace back to its summary. */}
            {session.hasRole('coach') ? (
              <>
                <p
                  data-testid="account-menu-workspace"
                  className="border-t border-hairline pt-2 text-[10px] font-bold uppercase tracking-[0.12em] text-ink-muted"
                >
                  Coaching workspace
                </p>
                <Link
                  to="/crm"
                  role="menuitem"
                  data-testid="account-workspace-link"
                  onClick={() => setOpen(false)}
                  className="py-2 text-[13px] font-bold text-ink hover:text-accent"
                >
                  Open the workspace
                </Link>
              </>
            ) : null}
            {/* The founder's portal is reachable from the two customer shells
                only here, in the account menu — never as a tab on their nav. */}
            {session.hasRole('admin') && audience !== 'admin' ? (
              <>
                <p className="border-t border-hairline pt-2 text-[10px] font-bold uppercase tracking-[0.12em] text-ink-muted">
                  Platform
                </p>
                <Link
                  to="/admin"
                  role="menuitem"
                  data-testid="account-admin-link"
                  onClick={() => setOpen(false)}
                  className="py-2 text-[13px] font-bold text-danger hover:text-accent"
                >
                  Platform admin
                </Link>
              </>
            ) : null}
            {/* 0038 — a support agent's own workspace, shown only to accounts
                the admin has actually put on the team. */}
            {user?.supportStaff ? (
              <Link
                to="/staff"
                role="menuitem"
                data-testid="account-staff-desk"
                onClick={() => setOpen(false)}
                className="border-t border-hairline py-2 text-[13px] font-bold text-accent hover:text-accent-hover"
              >
                Support desk
              </Link>
            ) : null}
            {/* Help is a conversation, not a page people hunt for:
                coaches meet it pinned in chat, everyone else reaches the same
                thread here. The admin answers support instead of writing it,
                so the operator's own menu omits it. */}
            {audience !== 'admin' ? (
              <Link
                to="/help"
                role="menuitem"
                data-testid="account-help-link"
                onClick={() => setOpen(false)}
                className="border-t border-hairline py-2 text-[13px] font-bold text-ink hover:text-accent"
              >
                Help
              </Link>
            ) : null}
            <Link
              to={
                audience === 'admin'
                  ? '/admin/profile'
                  : audience === 'crm'
                    ? '/crm/profile'
                    : '/profile'
              }
              role="menuitem"
              data-testid="account-profile-link"
              onClick={() => setOpen(false)}
              className="border-t border-hairline py-2 text-[13px] font-bold text-ink hover:text-accent"
            >
              Profile &amp; settings
            </Link>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
              className="border-t border-hairline py-2 text-left text-[13px] font-bold text-danger"
            >
              Sign out
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const session = useSession();
  const path = useRoute();
  const { unit, toggle } = useUnitPreference();
  const { theme } = useTheme();
  // Audience/route chooses the shell; the role is the access guard on the
  // gated routes, so a non-coach deep-linking `/crm` (or a non-admin at
  // `/admin`) sees the refusal panel in the lifter shell rather than a nav they
  // cannot use. The admin shell is a third, separate identity.
  const audience: 'lifter' | 'crm' | 'admin' =
    isAdminRoute(path) && session.hasRole('admin')
      ? 'admin'
      : isCoachRoute(path) && session.hasRole('coach')
        ? 'crm'
        : 'lifter';
  const nav = buildLifterNav(session);

  // Prefetch after paint: warm the roster for a coach/gym shell. Flagged, idle,
  // failure-silent — see lib/prefetch.ts.
  useEffect(() => {
    if (audience === 'crm') prefetchCoachRoster();
  }, [audience]);

  const signOut = () => {
    session.logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="l2f-app flex min-h-screen flex-col">
      <a
        href="#app-main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:border focus:border-accent focus:bg-surface focus:px-4 focus:py-2 focus:text-[13px] focus:font-bold focus:uppercase focus:text-ink"
      >
        Skip to content
      </a>

      <header className="sticky top-0 z-40 border-b border-hairline bg-surface/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-[1200px] 2xl:max-w-[1440px] min-[1920px]:max-w-[1720px] flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 md:px-8">
          {/* The logo always goes to the marketing home, from every in-app
              route. A plain anchor (not an in-app router Link) is deliberate:
              `/` is owned by the landing page, so this leaves the app entirely
              without disturbing the in-app nav beside it. */}
          <a
            href="/"
            data-testid="app-header-logo"
            aria-label="Lift2Fit home"
            className="flex shrink-0 items-center gap-3 text-ink"
          >
            <Logo variant="horizontal" className="h-7 w-[132px]" />
          </a>

          {/* The workspace tabs live HERE, beside the logo — the same place a
              lifter's tabs live — never in a second charcoal band below the
              header. The coach nav is tabs only (its name rides on the nav's
              aria-label); the three shells stay distinct: this slot renders the
              lifter nav, the workspace nav or the admin nav, never two at once,
              and a lifter still never sees a workspace tab. */}
          {audience === 'crm' ? (
            <nav
              aria-label="Coaching workspace"
              data-testid="crm-nav"
              className="order-last flex w-full min-w-0 items-center gap-1 overflow-x-auto lg:order-none lg:w-auto lg:flex-1"
            >
              {CRM_NAV.filter(
                (item) =>
                  !item.needsTeam ||
                  (session.user !== null &&
                    session.user.internalCoachOf === null &&
                    planIncludesTeam(session.user.coachPlan)),
              ).map((item) => {
                const active =
                  item.to === '/crm' ? path === '/crm' : path === item.to || path.startsWith(`${item.to}/`);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'inline-flex h-11 shrink-0 items-center border-b-2 px-3 text-[12px] font-bold uppercase tracking-[0.08em] transition-colors duration-150',
                      active ? 'border-accent text-ink' : 'border-transparent text-ink-muted hover:text-ink',
                    )}
                  >
                    {item.label}
                  </Link>
                );
              })}
              {/* No "Lifter app" cross-link here. It was pinned to the right of
                  eight workspace tabs with `ml-auto` and a left rule, so on the
                  widths a gym actually uses it read as a ninth tab hanging over
                  the edge of the row. A coach's own training is one click away
                  in the account menu ("Your training → Dashboard") and in the
                  footer, which is where every other secondary destination
                  already lives; the primary nav is the workspace only. */}
            </nav>
          ) : audience === 'admin' ? (
            /* The founder's portal is the admin shell's whole primary nav: nine
               surfaces, no lifter and no workspace tab. The two customer
               interfaces used to sit here as cross-links, which both crowded
               the row and offered a non-coach admin a dead end; they now live
               in the account menu, where every other secondary entry does. The
               row wraps rather than clips: before this, ten labels pushed
               System and Audit off the right edge behind a mid-word cut. A
               phone still scrolls sideways, where a wrap would eat the whole
               screen. */
            <nav
              aria-label="Platform admin"
              data-testid="admin-nav"
              className="order-last flex w-full min-w-0 flex-nowrap items-center gap-0.5 overflow-x-auto border-t-2 border-accent lg:order-none lg:w-auto lg:flex-1 lg:flex-wrap lg:overflow-visible lg:border-t-0"
            >
              {ADMIN_NAV.map((item) => {
                const active =
                  item.to === '/admin' ? path === '/admin' : path === item.to || path.startsWith(`${item.to}/`);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    aria-current={active ? 'page' : undefined}
                    // A little tighter than the customer navs: nine uppercase
                    // labels plus the header controls have to share one row at
                    // 1440, and the last tab (Audit) was falling to a second.
                    className={cn(
                      'inline-flex h-11 shrink-0 items-center border-b-2 px-1.5 text-[11.5px] font-bold uppercase tracking-[0.06em] transition-colors duration-150',
                      active ? 'border-accent text-ink' : 'border-transparent text-ink-muted hover:text-ink',
                    )}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          ) : (
            <nav aria-label="Primary" className="hidden min-w-0 items-center gap-1 overflow-x-auto lg:flex lg:flex-1">
              {nav.map((item) => {
                const active = path === item.to || path.startsWith(`${item.to}/`);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'inline-flex h-11 shrink-0 items-center gap-1 border-b-2 px-2.5 text-[12px] font-bold uppercase tracking-[0.08em] transition-colors duration-150',
                      active ? 'border-accent text-ink' : 'border-transparent text-ink-muted hover:text-ink',
                    )}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          )}

          <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={toggle}
              aria-label={`Switch to ${unit === 'metric' ? 'pounds' : 'kilograms'}`}
              className="inline-flex h-11 items-center gap-1 border border-hairline bg-surface px-2.5 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent"
            >
              <span className={cn('l2f-tabular font-mono', unit === 'metric' && 'text-accent')}>kg</span>
              <span className="text-ink-muted">/</span>
              <span className={cn('l2f-tabular font-mono', unit === 'imperial' && 'text-accent')}>lb</span>
            </button>

            <ThemeToggle />
            {/* 0028 polish: the coach's chat entry is the icon-only twin of the
                theme toggle — same 40×40 shape, chat glyph, unread badge. */}
            {audience === 'crm' ? <CoachChatIconButton /> : null}
            {/* Every shell reaches its chat from the same 40×40 glyph beside the
                theme toggle — the lifter's included, so the My Coach page no
                longer needs its own button. */}
            {audience === 'lifter' ? <LifterChatIconButton /> : null}
            {/* The operator's chat: the platform side of Help, in the
                same header slot the coach and lifter shells give their chat. */}
            {audience === 'admin' ? <AdminChatButton /> : null}
            <AccountMenu audience={audience} onSignOut={signOut} />
          </div>
        </div>

        {audience === 'lifter' ? (
          <nav aria-label="Primary mobile" className="flex gap-1 overflow-x-auto border-t border-hairline px-2 lg:hidden">
            {nav.map((item) => {
              const active = path === item.to || path.startsWith(`${item.to}/`);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'inline-flex h-11 shrink-0 items-center border-b-2 px-3 text-[12px] font-bold uppercase tracking-[0.08em]',
                    active ? 'border-accent text-ink' : 'border-transparent text-ink-muted',
                  )}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        ) : null}

        {session.isDemo ? <DemoBanner /> : null}
        {session.isDemo ? <DemoTrackControl /> : null}
      </header>

      <main id="app-main" className="mx-auto w-full max-w-[1200px] 2xl:max-w-[1440px] min-[1920px]:max-w-[1720px] flex-1 px-4 py-6 md:px-8 md:py-10">
        {children}
      </main>

      <footer className="border-t border-hairline bg-page-subtle" data-testid="app-footer">
        <div className="mx-auto flex w-full max-w-[1200px] 2xl:max-w-[1440px] min-[1920px]:max-w-[1720px] flex-wrap items-center justify-between gap-3 px-4 py-5 text-[12px] text-ink-muted md:px-8">
          <span className="flex items-center gap-2">
            <GridIcon className="text-[14px] text-accent" />
            Lift2Fit — Track it. Beat it.
          </span>
          <span className="flex items-center gap-3">
            {audience === 'crm' ? (
              <>
                <Link to="/crm/clients" className="hover:text-accent">
                  Clients
                </Link>
                <Link to="/crm/calendar" className="hover:text-accent">
                  Calendar
                </Link>
                <Link to="/dashboard" className="hover:text-accent">
                  Lifter app
                </Link>
              </>
            ) : audience === 'admin' ? (
              <>
                <Link to="/admin/businesses" className="hover:text-accent">
                  Businesses
                </Link>
                <Link to="/admin/revenue" className="hover:text-accent">
                  Revenue
                </Link>
                <Link to="/dashboard" className="hover:text-accent">
                  Lifter app
                </Link>
                <Link to="/crm" className="hover:text-accent">
                  Coaching workspace
                </Link>
              </>
            ) : (
              <>
                <Link to="/grid" className="hover:text-accent">
                  My Grid
                </Link>
                <Link to="/programme" className="hover:text-accent">
                  My Programme
                </Link>
                <Link to="/dashboard" className="hover:text-accent">
                  Dashboard
                </Link>
              </>
            )}
            {/* No Profile link here: the account menu's "Profile & settings" is the
                single entry point for account surfaces, in both shells. */}
            <span>Not medical advice</span>
            <span className="uppercase tracking-[0.08em]">{theme} theme</span>
          </span>
        </div>
      </footer>
    </div>
  );
}

/** Panel shown when a route needs a role the account does not hold yet. */
export function RoleRequired({ role }: { role: 'coach' | 'admin' }) {
  const title = role === 'coach' ? 'Coach access' : 'Admin access';
  const body =
    role === 'coach'
      ? 'The coaching workspace shows every client linked to your account and authors their programmes. Your account does not hold the coach role yet.'
      : 'The admin surface is the platform owner’s: every account, business, seat and payment across both products. It is granted deliberately at the database level and can never be self-served. Your account does not hold the admin role.';
  return (
    <div className="flex flex-col items-start gap-4 border border-hairline bg-surface p-6" data-testid="role-required">
      <h1 className="font-display text-h2 font-black uppercase text-ink">{title}</h1>
      <p className="max-w-[62ch] text-[15px] text-ink-muted">{body}</p>
      {role === 'admin' ? (
        <p className="text-[13px] text-ink-muted">
          If you believe this is wrong, the grant is a deliberate database change — the API has no
          self-promotion path, by design.
        </p>
      ) : (
        <p className="text-[13px] text-ink-muted">
          A coaching business starts from the CRM front door: sign up or sign in there and the workspace
          is yours. Nothing here grants the role silently.
        </p>
      )}
    </div>
  );
}

/**
 * Panel shown when a lifter route is opened by a coach whose plan does not
 * include the lifter interface (0021: the free, 1-client plan). One account,
 * two interfaces, one gated by the plan — so the reason is stated in one line
 * with the upgrade path, never a dead screen.
 */
export function LifterInterfaceRequired() {
  return (
    <div
      className="flex flex-col items-start gap-4 border border-hairline bg-surface p-6"
      data-testid="lifter-interface-required"
    >
      <h1 className="font-display text-h2 font-black uppercase text-ink">Your lifter profile</h1>
      <p className="max-w-[62ch] text-[15px] text-ink-muted">
        Your coaching plan is Free (1 client), which does not include the lifter interface. Upgrade to Coach
        (up to 10 clients) and your own training is included — a coaching workspace, plus a free lifter
        account for your own training.
      </p>
      <div className="flex flex-wrap gap-2">
        <Link
          to="/crm/profile"
          data-testid="lifter-interface-upgrade"
          className="inline-flex h-11 items-center justify-center gap-2 bg-accent px-5 text-[13px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover"
        >
          See your plan in Profile &amp; settings
        </Link>
        <Link
          to="/crm"
          className="inline-flex h-11 items-center justify-center gap-2 border border-hairline px-5 text-[13px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent"
        >
          Back to the workspace
        </Link>
      </div>
    </div>
  );
}

/** Short, honest sign-in gate used by protected routes. */
export function SignInRequired() {  const session = useSession();
  /**
   * A signed-out deep link must lead to the right door: a coaching business that
   * bookmarked `#/crm` belongs at the business login, not the lifter one.
   */
  const path = useRoute();
  const business = isCoachRoute(path);
  return (
    <div className="flex flex-col items-start gap-4 border border-hairline bg-surface p-6">
      <h1 className="font-display text-2xl font-black uppercase text-ink">Sign in to continue</h1>
      <p className="max-w-[62ch] text-[15px] text-ink-muted">
        {business
          ? 'This screen reads your clients. Sign in to your workspace, or start a business account.'
          : 'This screen reads your log. Sign in, or open the demo dataset to review every lifter screen without a backend.'}
      </p>
      <div className="flex flex-wrap gap-2">
        <Link
          to={business ? '/crm/login' : '/login'}
          className="inline-flex h-11 items-center justify-center gap-2 bg-accent px-5 text-[13px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover"
        >
          {business ? 'Business sign in' : 'Sign in'}
        </Link>
        {business ? (
          <Link
            to="/login"
            className="inline-flex h-11 items-center justify-center gap-2 border border-hairline px-5 text-[13px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent"
          >
            Lifter log in
          </Link>
        ) : (
          <Button variant="outline" onClick={() => session.loginDemo('lifter')}>
            Continue in demo mode
          </Button>
        )}
      </div>
    </div>
  );
}
